import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AuthenticationService } from '../services/authentication.service';

// Module-level state — shared across all interceptor invocations
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService  = inject(AuthService);
  const authHttp     = inject(AuthenticationService);
  const router       = inject(Router);

  // Never attach auth header or intercept 401 for the refresh endpoint itself
  if (req.url.includes('auth/refresh') || req.url.includes('auth/logout')) {
    return next(req);
  }

  const token = authService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) return throwError(() => err);

      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return refreshSubject.pipe(
          filter(t => t !== null),
          take(1),
          switchMap(newToken =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          )
        );
      }

      isRefreshing = true;
      refreshSubject.next(null);

      return authHttp.refreshToken().pipe(
        switchMap(() => {
          isRefreshing = false;
          const newToken = authService.getToken()!;
          refreshSubject.next(newToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
        }),
        catchError(refreshErr => {
          isRefreshing = false;
          authService.logout();
          router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
          return throwError(() => refreshErr);
        })
      );
    })
  );
};
